import React from 'react';
import { Button } from '../ui/button';
import { Card } from '../ui/card';
import { Badge } from '../ui/badge';
import { Code, Database, Briefcase, Mail } from 'lucide-react';

const ThemePreview = ({ colors }) => {
  const previewStyle = {
    '--preview-primary': colors.primary,
    '--preview-secondary': colors.secondary,
    '--preview-background': colors.background,
    '--preview-surface': colors.surface,
    '--preview-text': colors.text,
    '--preview-text-secondary': colors.textSecondary,
    '--preview-border': colors.border,
  };

  return (
    <div 
      className="space-y-6 p-6 rounded-3xl border-2"
      style={{
        ...previewStyle,
        backgroundColor: colors.background,
        borderColor: `${colors.border}33`,
      }}
    >
      {/* Header Preview */}
      <div className="space-y-2">
        <h2 
          className="text-3xl font-bold"
          style={{ color: colors.text }}
        >
          Mohammed Raheem
        </h2>
        <p style={{ color: colors.textSecondary }}>
          Full Stack Developer
        </p>
      </div>

      {/* Gradient Text Preview */}
      <div>
        <h3 
          className="text-2xl font-bold mb-2"
          style={{
            background: `linear-gradient(135deg, ${colors.primary} 0%, ${colors.secondary} 100%)`,
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
          }}
        >
          Building Digital Excellence
        </h3>
        <p style={{ color: colors.textSecondary }} className="text-sm">
          Crafting scalable web solutions with modern technologies
        </p>
      </div>

      {/* Buttons Preview */}
      <div className="flex flex-wrap gap-3">
        <button
          className="px-6 py-3 rounded-full font-medium transition-all hover:scale-105"
          style={{
            background: `linear-gradient(135deg, ${colors.primary}, ${colors.secondary})`,
            color: colors.text,
          }}
        >
          Primary Button
        </button>
        <button
          className="px-6 py-3 rounded-full font-medium transition-all hover:scale-105"
          style={{
            border: `2px solid ${colors.border}33`,
            color: colors.primary,
            backgroundColor: 'transparent',
          }}
        >
          Secondary Button
        </button>
      </div>

      {/* Card Preview */}
      <div
        className="p-6 rounded-2xl"
        style={{
          backgroundColor: colors.surface,
          border: `1px solid ${colors.border}20`,
        }}
      >
        <div className="flex items-start gap-4 mb-4">
          <div
            className="p-3 rounded-xl"
            style={{ backgroundColor: `${colors.primary}20` }}
          >
            <Code size={24} style={{ color: colors.primary }} />
          </div>
          <div className="flex-1">
            <h4 
              className="text-lg font-bold mb-1"
              style={{ color: colors.text }}
            >
              Project Card Example
            </h4>
            <p 
              className="text-sm"
              style={{ color: colors.textSecondary }}
            >
              This is how your project cards will look with the selected theme
            </p>
          </div>
        </div>
      </div>

      {/* Skills/Badges Preview */}
      <div className="space-y-3">
        <h4 
          className="text-sm font-semibold"
          style={{ color: colors.text }}
        >
          Skills Preview
        </h4>
        <div className="flex flex-wrap gap-2">
          {['React', 'TypeScript', 'Node.js', 'MongoDB'].map((skill, index) => (
            <span
              key={index}
              className="px-3 py-1.5 rounded-full text-xs font-medium"
              style={{
                backgroundColor: `${colors.primary}20`,
                color: colors.primary,
                border: `1px solid ${colors.primary}30`,
              }}
            >
              {skill}
            </span>
          ))}
        </div>
      </div>

      {/* Experience Timeline Preview */}
      <div
        className="p-5 rounded-xl"
        style={{
          backgroundColor: colors.surface,
          border: `1px solid ${colors.border}20`,
        }}
      >
        <div className="flex items-start gap-3">
          <div
            className="p-2 rounded-lg"
            style={{
              background: `linear-gradient(135deg, ${colors.primary}, ${colors.secondary})`,
            }}
          >
            <Briefcase size={20} style={{ color: colors.text }} />
          </div>
          <div className="flex-1">
            <h5 
              className="font-bold"
              style={{ color: colors.text }}
            >
              Software Engineer
            </h5>
            <p 
              className="text-sm"
              style={{ color: colors.primary }}
            >
              Tai Software
            </p>
            <p 
              className="text-xs mt-1"
              style={{ color: colors.textSecondary }}
            >
              Apr 2024 - Present
            </p>
          </div>
        </div>
      </div>

      {/* Contact Section Preview */}
      <div
        className="p-5 rounded-xl"
        style={{
          backgroundColor: `${colors.primary}10`,
          border: `1px solid ${colors.primary}20`,
        }}
      >
        <div className="flex items-center gap-3">
          <div
            className="p-2 rounded-full"
            style={{ backgroundColor: colors.primary }}
          >
            <Mail size={18} style={{ color: colors.text }} />
          </div>
          <div>
            <p 
              className="text-sm font-medium"
              style={{ color: colors.text }}
            >
              raheemprogrammer@gmail.com
            </p>
          </div>
        </div>
      </div>

      {/* Color Swatches */}
      <div className="space-y-2 pt-4 border-t" style={{ borderColor: `${colors.border}20` }}>
        <p className="text-xs font-semibold" style={{ color: colors.textSecondary }}>
          Color Palette
        </p>
        <div className="grid grid-cols-4 gap-2">
          <div className="space-y-1">
            <div 
              className="w-full h-8 rounded border"
              style={{ 
                backgroundColor: colors.primary,
                borderColor: colors.border 
              }}
            ></div>
            <p className="text-xs text-center" style={{ color: colors.textSecondary }}>Primary</p>
          </div>
          <div className="space-y-1">
            <div 
              className="w-full h-8 rounded border"
              style={{ 
                backgroundColor: colors.secondary,
                borderColor: colors.border 
              }}
            ></div>
            <p className="text-xs text-center" style={{ color: colors.textSecondary }}>Secondary</p>
          </div>
          <div className="space-y-1">
            <div 
              className="w-full h-8 rounded border"
              style={{ 
                backgroundColor: colors.surface,
                borderColor: colors.border 
              }}
            ></div>
            <p className="text-xs text-center" style={{ color: colors.textSecondary }}>Surface</p>
          </div>
          <div className="space-y-1">
            <div 
              className="w-full h-8 rounded border"
              style={{ 
                backgroundColor: colors.border,
                borderColor: colors.border 
              }}
            ></div>
            <p className="text-xs text-center" style={{ color: colors.textSecondary }}>Border</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ThemePreview;
